/**
 * Houseboat Studios shared TV-mode convention (Fire TV WebView).
 *
 * TV mode activates when the URL carries ?tv=1 or the user agent looks like
 * a Fire TV device (AFT* model codes or an explicit "Fire TV" token). Once
 * detected it is persisted for the session under sessionStorage 'hbs_tv' so
 * in-app navigations that drop the query param stay in TV mode.
 *
 * All TV-specific behavior in the app must be gated on isTvMode() (JS) or
 * the html.tv-mode class (CSS) so desktop/mobile play is never affected.
 */

const TV_SESSION_KEY = 'hbs_tv'
const FIRE_TV_MODEL_UA = /AFT[A-Z0-9]/i
const FIRE_TV_NAME_UA = /Fire ?TV/i

let cachedTvMode: boolean | null = null

const readSessionFlag = (): boolean => {
  try {
    return window.sessionStorage.getItem(TV_SESSION_KEY) === '1'
  } catch {
    // Storage can be unavailable (privacy modes); fall back to detection.
    return false
  }
}

const persistSessionFlag = (): void => {
  try {
    window.sessionStorage.setItem(TV_SESSION_KEY, '1')
  } catch {
    // Non-fatal: detection will simply re-run on the next load.
  }
}

/** True when the game should behave as a Fire TV (D-pad) experience. */
export const isTvMode = (): boolean => {
  if (cachedTvMode !== null) return cachedTvMode

  const query = new URLSearchParams(window.location.search)
  const detected =
    query.get('tv') === '1' ||
    FIRE_TV_MODEL_UA.test(navigator.userAgent) ||
    FIRE_TV_NAME_UA.test(navigator.userAgent) ||
    readSessionFlag()

  if (detected) persistSessionFlag()
  cachedTvMode = detected
  return detected
}

/**
 * Run once at boot (before first render). Tags <html> with the tv-mode
 * class that every TV-only CSS rule hangs off of.
 */
export const initTvMode = (): boolean => {
  const tv = isTvMode()
  if (tv) document.documentElement.classList.add('tv-mode')
  return tv
}
